import { useMemo, useState } from 'react';
import { useEditor, EditorContent, useEditorState } from '@tiptap/react';
import { Placeholder } from '@tiptap/extensions';
import clsx from 'clsx';
import Icon from '../components/Icon.jsx';
import { getExtensions } from './extensions.js';
import { isDoc, textToDoc } from './content.js';
import { enableBlanks, withBlanks } from './blanks.js';
import { blankAt } from './blankExtension.js';
import styles from './RichEditor.module.scss';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform ?? '');
const BLANK_SHORTCUT = isMac ? '⌘⌥B' : 'Ctrl+Alt+B';

const COLORS = [
  { label: 'Default', value: null },
  { label: 'Red', value: '#d9472b' },
  { label: 'Blue', value: '#2f6fb3' },
  { label: 'Green', value: '#2e7d4f' },
  { label: 'Amber', value: '#c27c0e' },
];

// Act on pointerdown. Cancelling it also cancels the later click, and a click
// handler would run twice when the toolbar re-renders mid-press.
function pressProps(run, disabled) {
  return {
    onPointerDown: (e) => {
      if (e.button !== 0 || disabled) return;
      e.preventDefault();
      run();
    },
    onKeyDown: (e) => {
      if (disabled || (e.key !== 'Enter' && e.key !== ' ')) return;
      e.preventDefault();
      run();
    },
  };
}

function ToolButton({ active, onClick, label, children, disabled }) {
  return (
    <button
      type="button"
      className={clsx(styles.tool, active && styles.toolOn)}
      {...pressProps(onClick, disabled)}
      aria-pressed={active}
      aria-label={label}
      title={label}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

function Toolbar({ editor, cloze }) {
  const [more, setMore] = useState(false);
  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => {
      let blankMax = 0;
      if (cloze) {
        e.state.doc.descendants((node) => {
          if (node.type.name === 'blank') blankMax = Math.max(blankMax, Number(node.attrs.n) || 0);
        });
      }
      const inside = blankAt(e.state.selection);
      return {
        bold: e.isActive('bold'),
        italic: e.isActive('italic'),
        underline: e.isActive('underline'),
        strike: e.isActive('strike'),
        highlight: e.isActive('highlight'),
        code: e.isActive('code'),
        bullet: e.isActive('bulletList'),
        ordered: e.isActive('orderedList'),
        color: e.getAttributes('textStyle').color ?? null,
        blankMax,
        inside: !!inside,
      };
    },
  });
  const chain = () => editor.chain().focus();

  return (
    <div className={styles.bar}>
      <div className={styles.toolbar} role="toolbar" aria-label="Formatting">
        {cloze ? (
          <>
            <button
              type="button"
              className={clsx(styles.blankBtn, state.inside && styles.blankBtnOff)}
              {...pressProps(() => editor.chain().focus().insertBlank(false).run())}
              title={
                state.inside
                  ? 'Put this word back into the sentence'
                  : `Hide a word (${BLANK_SHORTCUT}). Click a word, or select a few.`
              }
            >
              <Icon name={state.inside ? 'close' : 'blank'} size={18} />
              {state.inside ? 'Remove' : 'Blank'}
            </button>
            {state.blankMax > 0 && !state.inside ? (
              <button
                type="button"
                className={styles.textTool}
                {...pressProps(() => editor.chain().focus().insertBlank(true).run())}
                title="Hide this word on the same card as the previous blank"
              >
                Same card
              </button>
            ) : null}
          </>
        ) : null}
        <ToolButton label="Bold" active={state.bold} onClick={() => chain().toggleBold().run()}>
          <b>B</b>
        </ToolButton>
        <ToolButton label="Italic" active={state.italic} onClick={() => chain().toggleItalic().run()}>
          <i>I</i>
        </ToolButton>
        <ToolButton label="Highlight" active={state.highlight} onClick={() => chain().toggleHighlight().run()}>
          <span className={styles.hl}>H</span>
        </ToolButton>
        <ToolButton label="Bullet list" active={state.bullet} onClick={() => chain().toggleBulletList().run()}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
            <path d="M9 6h11M9 12h11M9 18h11M4.5 6h.01M4.5 12h.01M4.5 18h.01" />
          </svg>
        </ToolButton>
        <ToolButton label={more ? 'Fewer options' : 'More formatting'} active={more} onClick={() => setMore((m) => !m)}>
          <span className={styles.aa}>Aa</span>
        </ToolButton>
      </div>
      {more ? (
        <div className={styles.toolbar} role="toolbar" aria-label="More formatting">
          <ToolButton label="Underline" active={state.underline} onClick={() => chain().toggleUnderline().run()}>
            <u>U</u>
          </ToolButton>
          <ToolButton label="Strikethrough" active={state.strike} onClick={() => chain().toggleStrike().run()}>
            <s>S</s>
          </ToolButton>
          <ToolButton label="Numbered list" active={state.ordered} onClick={() => chain().toggleOrderedList().run()}>
            <span className={styles.mono}>1.</span>
          </ToolButton>
          <ToolButton label="Code" active={state.code} onClick={() => chain().toggleCode().run()}>
            <span className={styles.mono}>{'</>'}</span>
          </ToolButton>
          {COLORS.map((c) => (
            <ToolButton
              key={c.label}
              label={`Text color: ${c.label}`}
              active={state.color === c.value}
              onClick={() => (c.value ? chain().setColor(c.value).run() : chain().unsetColor().run())}
            >
              <span className={styles.swatch} style={{ background: c.value ?? 'var(--text)' }} />
            </ToolButton>
          ))}
          <button
            type="button"
            className={styles.textTool}
            {...pressProps(() => chain().unsetAllMarks().clearNodes().run())}
          >
            Clear style
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** Rich-text field editor. Emits TipTap JSON, or null when empty. */
export default function RichEditor({ value, onChange, placeholder, cloze = false, autoFocus, id, label }) {
  const content = useMemo(() => {
    const doc = isDoc(value) ? value : value ? textToDoc(String(value)) : null;
    return cloze && doc ? withBlanks(doc) : doc;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const editor = useEditor({
    extensions: [...getExtensions(), Placeholder.configure({ placeholder: placeholder ?? '' })],
    content,
    autofocus: autoFocus ? 'end' : false,
    editorProps: {
      attributes: {
        class: clsx('rich', styles.content),
        id: id ?? '',
        'aria-label': label ?? 'Field',
        role: 'textbox',
        'aria-multiline': 'true',
        autocapitalize: 'sentences',
      },
    },
    onCreate: ({ editor: created }) => {
      if (cloze) enableBlanks(created);
    },
    onUpdate: ({ editor: e }) => onChange?.(e.isEmpty ? null : e.getJSON()),
  });

  if (!editor) return <div className={styles.shell} />;

  return (
    <div className={styles.shell}>
      <EditorContent editor={editor} />
      <Toolbar editor={editor} cloze={cloze} />
    </div>
  );
}
